import React, { useState, useEffect, useRef, useContext, useCallback, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FaBell } from 'react-icons/fa';
import { BsChatLeftText } from 'react-icons/bs';
import Modal from './Modal';
import DarkModeContext from './DarkModeContext';
import { useHistory } from 'react-router-dom';
import { useAtom } from 'jotai';
import { userRoleAtom } from '../Services/store/atoms';
import NotificationItem from './NotificationItem';
import useNotifications from '../hooks/useNotifications';
import useMediaQuery from '../hooks/useMediaQuery';
import '../Styles/bellNotifications.css';

const DROPDOWN_WIDTH = 320; // w-80
const VIEWPORT_MARGIN = 12; // separación mínima con los bordes de la pantalla

const BellNotifications = ({ isAuth, authUser }) => {
  const { notifications, hasChatNotifications, markAsViewed, markManyAsViewed, markAllAsViewed } = useNotifications({
    isAuth,
    token: authUser?.token,
  });

  const [showDropdown, setShowDropdown] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [isRinging, setIsRinging] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [panelStyle, setPanelStyle] = useState(null);
  const prevCountRef = useRef(0);
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const { darkMode } = useContext(DarkModeContext);
  const [userRole] = useAtom(userRoleAtom);
  const history = useHistory();
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const isOpen = showDropdown || showMobileModal;

  // Lo que el usuario tuvo a la vista mientras el panel estaba abierto se da por
  // visto al cerrarlo; si no, el contador del header no bajaba nunca.
  const shownIdsRef = useRef(new Set());
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  useEffect(() => {
    if (isOpen) {
      notificationsRef.current.forEach((n) => shownIdsRef.current.add(n.id));
      return undefined;
    }
    const seen = shownIdsRef.current;
    if (seen.size === 0) return undefined;
    markManyAsViewed(notificationsRef.current.filter((n) => seen.has(n.id)));
    shownIdsRef.current = new Set();
    return undefined;
  }, [isOpen, markManyAsViewed]);

  // La campana "pulsa" y se anuncia a lectores de pantalla solo cuando
  // llegan notificaciones nuevas, no al descartar
  useEffect(() => {
    const prevCount = prevCountRef.current;
    prevCountRef.current = notifications.length;
    if (notifications.length > prevCount) {
      const added = notifications.length - prevCount;
      setAnnouncement(
        added === 1
          ? 'Tienes 1 notificación nueva'
          : `Tienes ${added} notificaciones nuevas`
      );
      setIsRinging(true);
      const timer = setTimeout(() => setIsRinging(false), 400);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [notifications.length]);

  const closeAll = () => {
    setShowDropdown(false);
    setShowMobileModal(false);
  };

  const toggleNotifications = () => {
    if (isDesktop) {
      setShowDropdown((prev) => !prev);
    } else {
      setShowMobileModal(true);
    }
  };

  // Cerrar el dropdown de escritorio con Escape o clic fuera.
  // El modal mobile maneja su propio Escape/clic-afuera/foco vía Dialog.
  useEffect(() => {
    if (!showDropdown) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setShowDropdown(false);
    };
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Posición del dropdown calculada en base al botón real, en vez de asumir
  // que el layout que lo rodea le deja suficiente espacio a la derecha: así
  // nunca se sale de la pantalla (ni por izquierda ni por derecha) sin
  // importar el ancho del sidebar, zoom, o el viewport disponible.
  const updatePanelPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const width = Math.min(DROPDOWN_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    const maxLeft = Math.max(window.innerWidth - width - VIEWPORT_MARGIN, VIEWPORT_MARGIN);
    const left = Math.min(Math.max(rect.right - width, VIEWPORT_MARGIN), maxLeft);
    setPanelStyle({ top: rect.bottom + 8, left, width });
  }, []);

  useEffect(() => {
    if (!showDropdown) return undefined;

    updatePanelPosition();
    window.addEventListener('resize', updatePanelPosition);
    window.addEventListener('scroll', updatePanelPosition, true);
    return () => {
      window.removeEventListener('resize', updatePanelPosition);
      window.removeEventListener('scroll', updatePanelPosition, true);
    };
  }, [showDropdown, updatePanelPosition]);

  const handleNotificationClick = (notification) => {
    closeAll();
    modalRef.current?.openModal(
      'info',
      notification.title,
      <p className={`${darkMode ? 'text-white' : 'text-black'}`}>
        {notification.body || 'No additional details'}
      </p>
    );
    markAsViewed(notification);
  };

  const handleNotificationDismiss = (notification) => {
    if (notifications.length === 1) closeAll();
    markAsViewed(notification);
  };

  const handleMarkAllAsRead = () => {
    markAllAsViewed();
    closeAll();
  };

  const renderNotificationList = () =>
    notifications.map((notification) => (
      <NotificationItem
        key={notification.id}
        notification={notification}
        onClick={handleNotificationClick}
        onDismiss={handleNotificationDismiss}
        darkMode={darkMode}
        navigate={history.push}
        userRole={userRole}
      />
    ));

  const renderEmptyState = () => (
    <div className="p-8 flex flex-col items-center gap-2 text-center text-gray-500 dark:text-gray-400">
      <FaBell className="text-3xl opacity-30" aria-hidden="true" />
      <p className="text-sm font-medium">You're all caught up</p>
      <p className="text-xs">No new notifications</p>
    </div>
  );

  const renderListHeader = () =>
    notifications.length > 0 && (
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
          {notifications.length} unread
        </span>
        <button
          onClick={handleMarkAllAsRead}
          className="text-xs font-semibold text-rose-500 hover:text-rose-600"
        >
          Mark all as read
        </button>
      </div>
    );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Anuncio para lectores de pantalla cuando llegan notificaciones nuevas */}
      <span className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {announcement}
      </span>

      <button
        ref={buttonRef}
        onClick={toggleNotifications}
        className="relative text-rose-500 hover:text-rose-700 focus:outline-none w-6 h-6"
        aria-label={`Notifications${notifications.length > 0 ? ` (${notifications.length} unread)` : ''}`}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        {hasChatNotifications ? (
          <BsChatLeftText className={`w-6 h-6 ${isRinging ? 'bell-ringing' : ''}`} />
        ) : (
          <FaBell className={`w-6 h-6 ${isRinging ? 'bell-ringing' : ''}`} />
        )}
        {notifications.length > 0 && (
          <span
            key={notifications.length}
            className="bell-badge-pop absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-white text-gray-800 text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center"
          >
            {notifications.length > 99 ? '99+' : notifications.length}
          </span>
        )}
      </button>

      {/* 📌 Desktop: Dropdown de notificaciones — misma transición estándar
          (fade + scale, 100ms entrada / 75ms salida) que usa el resto de la app.
          Posición "fixed" calculada en JS (updatePanelPosition) para que nunca
          se salga de la pantalla, en vez de "absolute right-0" que asume que
          siempre hay ~320px libres a la izquierda del botón. */}
      <Transition
        show={showDropdown && isDesktop && !!panelStyle}
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div
          role="menu"
          aria-orientation="vertical"
          style={panelStyle ? { top: panelStyle.top, left: panelStyle.left, width: panelStyle.width } : undefined}
          className="fixed max-h-96 origin-top-right bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-sm z-50 overflow-y-auto"
        >
          {renderListHeader()}
          {notifications.length === 0 ? renderEmptyState() : renderNotificationList()}
        </div>
      </Transition>

      {/* 📱 Móvil: Modal con foco atrapado (mismo patrón que Modal.jsx) */}
      <Transition.Root show={showMobileModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          initialFocus={closeButtonRef}
          onClose={setShowMobileModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="bg-white dark:bg-gray-900 rounded-xl shadow-sm w-11/12 max-w-lg max-h-[90dvh] flex flex-col overflow-hidden">
                <div className="shrink-0 p-4 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white font-semibold flex items-center justify-between border-b dark:border-gray-700">
                  <Dialog.Title as="div" className="flex items-center">
                    {hasChatNotifications ? (
                      <BsChatLeftText className="mr-2 text-xl text-rose-500" />
                    ) : (
                      <FaBell className="mr-2 text-xl text-rose-500" />
                    )}
                    Notifications
                  </Dialog.Title>
                  <button
                    onClick={() => setShowMobileModal(false)}
                    className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    aria-label="Close notifications"
                  >
                    ✖
                  </button>
                </div>

                {/* Área scrolleable: crece/encoge según el espacio libre, el
                    header y el botón de cerrar (shrink-0) siempre quedan visibles */}
                <div className="flex-1 min-h-0 overflow-y-auto">
                  {renderListHeader()}
                  <div className="p-4">
                    {notifications.length === 0 ? renderEmptyState() : renderNotificationList()}
                  </div>
                </div>

                <div className="shrink-0 p-4 bg-gray-100 dark:bg-gray-800 text-center">
                  <button
                    ref={closeButtonRef}
                    onClick={() => setShowMobileModal(false)}
                    className="w-full bg-rose-500 hover:bg-rose-600 text-white font-semibold py-2 rounded-xl"
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      <Modal ref={modalRef} />
    </div>
  );
};

export default BellNotifications;
